import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { FiExternalLink, FiFileText, FiUpload } from "react-icons/fi";

import { getApiUrl } from "../../../utils/apiUrl";
import { getCsrfToken } from "../../../utils/getCsrfToken";

const DocumentsSection = ({
  actividadId,
  detailPath = "api/perfiles/actividades",
  detailAction = "materiales",
  collectionPath = "api/perfiles/materiales/",
  foreignKey = "actividad",
}) => {
  const [newDocument, setNewDocument] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentName, setDocumentName] = useState("");
  const [inputKey, setInputKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchDocuments = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await fetch(
        getApiUrl(`${detailPath}/${actividadId}/${detailAction}/`),
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("No se pudieron cargar los documentos.");
      const data = await response.json();
      setDocuments(Array.isArray(data) ? data : []);
    } catch (requestError) {
      setDocuments([]);
      setError(requestError.message);
    } finally {
      setIsLoading(false);
    }
  }, [actividadId, detailAction, detailPath]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleDocumentSubmit = async () => {
    if (!newDocument || !documentName.trim()) {
      setError("Indica un nombre y selecciona un archivo PDF.");
      return;
    }

    setIsSaving(true);
    setError("");
    try {
      const csrfToken = await getCsrfToken();
      const formData = new FormData();
      formData.append("documento", newDocument);
      formData.append(foreignKey, actividadId);
      formData.append("nombre", documentName.trim());

      const response = await fetch(getApiUrl(collectionPath), {
        method: "POST",
        headers: { "X-CSRFToken": csrfToken },
        body: formData,
        credentials: "include",
      });
      if (!response.ok) throw new Error("No se pudo subir el documento.");
      setNewDocument(null);
      setDocumentName("");
      setInputKey((current) => current + 1);
      await fetchDocuments();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="actividad-info initiative-documents-section">
      <div className="initiative-section-heading initiative-tab-heading">
        <div><span>ARCHIVOS</span><h5>Documentos</h5><p>{documents.length} documentos guardados</p></div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <section className="initiative-tab-form-card initiative-document-form">
        <Form.Group>
          <Form.Label>Nombre del documento</Form.Label>
          <Form.Control
            type="text"
            placeholder="Ej.: Lista de materiales"
            value={documentName}
            onChange={(event) => setDocumentName(event.target.value)}
            disabled={isSaving}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Archivo PDF</Form.Label>
          <Form.Control
            key={inputKey}
            type="file"
            accept=".pdf,application/pdf"
            onChange={(event) => setNewDocument(event.target.files?.[0] || null)}
            disabled={isSaving}
          />
        </Form.Group>
        <Button
          type="button"
          className="initiative-tab-primary"
          onClick={handleDocumentSubmit}
          disabled={!newDocument || !documentName.trim() || isSaving}
        >
          {isSaving ? <Spinner animation="border" size="sm" /> : <FiUpload />}
          Subir PDF
        </Button>
      </section>

      {isLoading ? (
        <div className="initiative-attendance-loading"><Spinner animation="border" size="sm" /> Cargando documentos…</div>
      ) : documents.length > 0 ? (
        <div className="initiative-document-list">
          {documents.map((document) => (
            <a
              key={document.id}
              className="initiative-document-item"
              href={document.documento}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="initiative-document-icon"><FiFileText /></span>
              <span className="initiative-document-copy">
                <strong>{document.nombre}</strong>
                <small>{document.date_observed}</small>
              </span>
              <FiExternalLink className="initiative-document-open" />
            </a>
          ))}
        </div>
      ) : (
        <div className="initiative-tab-empty"><FiFileText /><strong>Sin documentos</strong><span>Todavía no hay archivos guardados.</span></div>
      )}
    </div>
  );
};

export default DocumentsSection;
